/**
 * VitalCity - shared scroll-in animation primitives.
 *
 * Ported VERBATIM from the Gen Z dashboard (ProjectSignalDashboardV6.js,
 * ~L240-434) so VitalCity animates numbers and bars exactly like Gen Z:
 *   - CountUp        : a number counts 0 -> value the FIRST time it scrolls
 *                      into view (also on first load if already in view),
 *                      eased on the same bezier the bars use. It does NOT
 *                      replay on later scrolls; later value changes snap.
 *   - AnimatedBarFill: a single horizontal fill grows from 0% with a
 *                      leading-edge shimmer the first time it scrolls in.
 *   - useInViewOnce  : IntersectionObserver "fire once" hook.
 *
 * USAGE RULE (matches Gen Z): only animate a number when it is a standalone
 * stat-card value OR the value that labels an animated bar. NEVER animate a
 * number that sits inside a sentence / heading / legend / inline annotation.
 *
 * One source of truth shared by VitalCityDashboard.js, VitalCityNewTabs.js
 * and VitalCitySurveyOverview.js.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import ReactECharts from "echarts-for-react";
import { Tooltip as AntTooltip } from "antd";

/* IntersectionObserver-backed "fire once when scrolled into view" hook.
   `inView` flips true the FIRST time the element enters the viewport (or
   immediately on mount if already visible) and stays true after - so
   subsequent scroll in/out doesn't replay the animation. */
export function useInViewOnce({ rootMargin = "0px 0px -10% 0px", threshold = 0.15 } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            obs.disconnect();
            return;
          }
        }
      },
      { rootMargin, threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [inView, rootMargin, threshold]);
  return [ref, inView];
}

/* Leading-edge shimmer that travels with the bar as it grows. */
const barShimmerKeyframes = keyframes`
  0%   { opacity: 0; }
  12%  { opacity: 0.95; }
  35%  { opacity: 0.9; }
  60%  { opacity: 0.7; }
  78%  { opacity: 0.45; }
  90%  { opacity: 0.2; }
  97%  { opacity: 0.06; }
  100% { opacity: 0; }
`;

const BarShimmerStrip = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 26px;
  border-radius: 30px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.85) 70%,
    rgba(255, 255, 255, 0) 100%
  );
  filter: blur(1.5px);
  opacity: 0;
  pointer-events: none;
  animation: ${barShimmerKeyframes} ${(p) => p.$duration || 2400}ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
`;

/* Tooltip ONLY when the text is actually cut (user 2026-08-19: "tooltip should
   only come when elipsed"). Attach the ref to a clamped element; `truncated`
   is true only while its content overflows, so callers can render a real
   tooltip instead of a permanent title= on text that fits. Re-measures on
   resize and whenever the text changes. */
export function useTruncated(text) {
  const ref = useRef(null);
  const [truncated, setTruncated] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const cut = el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
      setTruncated((prev) => (prev === cut ? prev : cut));
    };
    measure();
    if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => measure()).catch(() => {});
    }
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text]);
  return [ref, truncated];
}

/* Text that clamps and shows a tooltip ONLY when it is actually cut (user
   2026-08-19). Wraps the shared rule so every label surface behaves the same:
   no permanent title= on text that fits, no silent truncation on text that
   does not. `lines` controls the clamp (1 = single line ellipsis). */
export function ClampText({ text, lines = 1, className, style, as: Tag = "span", tooltip = true }) {
  const [ref, truncated] = useTruncated(text);
  const clampStyle = lines > 1
    ? { display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" }
    : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
  const node = (
    <Tag ref={ref} className={className} style={{ minWidth: 0, ...clampStyle, ...style }}>
      {text}
    </Tag>
  );
  /* ALWAYS wrapped: flipping between bare node and Tooltip-wrapped remounts
     the element and the tooltip never fires (found 2026-08-20 via the rail
     quotes). An empty title = antd never opens, so untruncated text still
     shows nothing. */
  return <AntTooltip title={tooltip && truncated ? text : ""} styles={{ root: { maxWidth: 380 } }} mouseEnterDelay={0.35}>{node}</AntTooltip>;
}

/* Bar inner fill that animates from 0% to its target width the FIRST time
   the row scrolls into view. Subsequent target changes snap to the new
   width instantly so the bar always reflects current data. */
export function AnimatedBarFill({
  widthPct,
  background,
  className = "bar-fill",
  duration = 2400,
  style,
  inViewOverride,
}) {
  const [ref, inViewLocal] = useInViewOnce();
  const inView = inViewOverride != null ? inViewOverride : inViewLocal;
  /* LATCHED reveal (user 2026-08-19 "bars are bouncing"): re-ordering rows
     (sort pills) moves these nodes in the DOM, which re-fires the observer;
     a momentary false collapsed the bar to 0 and it grew again - the bounce.
     Once shown, always shown. */
  const shownRef = useRef(false);
  if (inView) shownRef.current = true;
  const shown = shownRef.current;
  const animationStartedRef = useRef(false);
  if (shown && !animationStartedRef.current) {
    animationStartedRef.current = true;
  }
  const w = shown ? `${widthPct}%` : "0%";
  return (
    <div
      ref={ref}
      className={className}
      style={{
        width: w,
        background,
        position: "relative",
        /* Transition is ALWAYS present (was applied in the same commit as the
           width change, which made the first growth race and sometimes snap).
           Width holds at 0 until reveal, so there is nothing to animate before
           then. Narrow screens keep the ease at half the travel time. */
        transition: `width ${typeof window !== "undefined" && window.innerWidth < 760 ? Math.min(duration, 700) : duration}ms cubic-bezier(0.22, 0.61, 0.36, 1)`,
        ...style,
      }}
    >
      {animationStartedRef.current && (
        <BarShimmerStrip key="shimmer" $duration={duration} aria-hidden />
      )}
    </div>
  );
}

/* Bezier solver matching the CSS cubic-bezier(0.22, 0.61, 0.36, 1) curve the
   bars transition with, so the number eases the SAME way the bar width does. */
function cubicBezier(x1, y1, x2, y2) {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t) => ((ay * t + by) * t + cy) * t;
  const sampleDerivX = (t) => (3 * ax * t + 2 * bx) * t + cx;
  return (x) => {
    let t = x;
    for (let i = 0; i < 6; i++) {
      const cur = sampleX(t) - x;
      const d = sampleDerivX(t);
      if (Math.abs(cur) < 1e-6) return sampleY(t);
      if (Math.abs(d) < 1e-6) break;
      t = t - cur / d;
    }
    let lo = 0;
    let hi = 1;
    t = x;
    for (let i = 0; i < 20; i++) {
      const cur = sampleX(t);
      if (Math.abs(cur - x) < 1e-6) return sampleY(t);
      if (cur < x) lo = t;
      else hi = t;
      t = (lo + hi) / 2;
    }
    return sampleY(t);
  };
}

export const easeOutBarCurve = cubicBezier(0.22, 0.61, 0.36, 1);

/* Scroll-gated ECharts. Matches Gen Z (V6 ~L5509-5519): the chart is only
   MOUNTED the first time its container scrolls into view, so ECharts' own
   draw-in animation fires on scroll-into-view (not on page/tab load). The
   wrapper fills its parent (which must have a defined height) and holds the
   space as a placeholder before the chart mounts, so there is no layout
   shift. All props (option, style, notMerge, lazyUpdate, etc.) pass through. */
export function RevealECharts(props) {
  const [ref, inView] = useInViewOnce();
  return (
    <div ref={ref} style={{ width: "100%", height: "100%" }}>
      {inView ? <ReactECharts {...props} /> : null}
    </div>
  );
}

/* Animated number counter. On first scroll-into-view (or first load if
   already visible) counts 0 -> value with the bar easing; later prop changes
   update immediately (no re-animation). */
export function CountUp({
  value,
  duration = 2400,
  suffix = "",
  prefix = "",
  decimals = 0,
  format,
  className,
  style,
}) {
  const [ref, inView] = useInViewOnce();
  const [display, setDisplay] = useState(0);
  const hasAnimatedRef = useRef(false);
  useEffect(() => {
    if (value == null || Number.isNaN(value)) return undefined;
    if (hasAnimatedRef.current) {
      setDisplay(value);
      return undefined;
    }
    if (!inView) return undefined;
    hasAnimatedRef.current = true;
    const start = performance.now();
    const to = Number(value);
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = easeOutBarCurve(t);
      setDisplay(to * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setDisplay(to);
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [inView, value, duration]);
  const renderFor = (v) => {
    if (format) return format(v);
    const fixed = decimals > 0 ? v.toFixed(decimals) : Math.round(v).toString();
    return `${prefix}${fixed}${suffix}`;
  };
  const rendered = renderFor(display);
  /* Reserve the FINAL width from the first frame (user 2026-08-19: text beside
     the bars moved up and down while the counter ran). "0%" is narrower than
     "91%", so the row rewrapped mid-animation; tabular figures + a min-width
     sized to the final string keep the layout still. */
  const finalText = value == null || Number.isNaN(value) ? rendered : renderFor(Number(value));
  /* Reserve the FINAL text's exact width so the counter can never resize its
     column mid-animation (a ch-based guess fell ~3px short - "%" is wider than
     a digit). A hidden sizer is rendered for ONE layout pass, measured, then
     dropped, so the committed DOM holds a single copy of the text. */
  const sizerRef = useRef(null);
  const [reserved, setReserved] = useState(null);
  useLayoutEffect(() => {
    setReserved(null);
  }, [finalText]);
  useLayoutEffect(() => {
    if (reserved != null || !sizerRef.current) return;
    const w = sizerRef.current.getBoundingClientRect().width;
    if (w > 0) setReserved(w);
  });
  return (
    <span
      ref={ref}
      className={className}
      style={{
        display: "inline-block",
        fontVariantNumeric: "tabular-nums",
        fontFeatureSettings: '"tnum"',
        ...(reserved != null ? { minWidth: `${Math.ceil(reserved)}px` } : null),
        ...style,
      }}
    >
      {reserved == null
        ? <span ref={sizerRef} aria-hidden="true" style={{ visibility: "hidden" }}>{finalText}</span>
        : rendered}
    </span>
  );
}
